import { FunctionComponent, useState, useEffect } from "react";
import React from "react";
import { useSelector } from "react-redux";
import { useFlags } from "launchdarkly-react-client-sdk";
import moment from "moment";
import { useTranslation } from "react-i18next";

import useAPI from "../../yoello-lib/modules/API/useAPI";
import { useAPIV2 } from "../../yoello-lib/modules/API2/useAPIv2";
import APIExtensions from "../../yoello-lib/modules/API2/APIDefs";
import { APIMerchantDashboard } from "../../yoello-lib/modules/API/apiDefs/MerchantDashboardAPIDefs";
// import { useAuth0 } from "../../yoello-lib/modules/auth/Auth0";
import EColourPanel from "../../interfaces/EColourPanel";
// import ProfileTitle from "../profile/ProfileTitle";
import generateStockReportFile, { IStockReportItem } from './StockReport';
import generateVatReport, { IVatReportItem } from './generateVatReport';
import generateOrderReportFile, { IOrderReportItem } from './OrderReport';
import generateTransactionFeeReportFile, { ITransactionFeeReportItem } from './TransactionFeeReport';
import generateStockByAreaReportFile from './AreaStockReport';
import orderLineItemDetailReport , { IOrderLineItemDetailReport } from './OrderLineItemDetailReport';
import IReportObject from './IReport';
import { HTTPMethods } from "../../yoello-lib/modules/API/API";
import { useSnackbarMessages } from "../../yoello-lib/modules/components/snackbar/SnackbarContext";
import { CircularProgress, Paper } from "@material-ui/core";
import { IMessageOptions } from "../../hooks/useAPI";
import { useCurrencyState } from "../../yoello-lib/modules/hooks/useCurrencyState";
import { generateTotalReport } from "./TotalReport";
import { toUTCDate } from "../../yoello-lib/modules/utils/TimeConversion";
import { IReduxAppState } from "../../reducers";
import { createReportFormRows } from "../../formSchemas/report.form";
import DynamicForm from "../../yoello-lib/modules/dynamicForms/DynamicForms";
import { OnboardingSource } from "../menu/payments/types.d";
import { ReactComponent as DownloadSVG } from "../../assets/images/icon-download.svg";
import { useStyles } from "./styles";
import { useAuthenticationContext } from "../../yoello-lib/modules/auth/AuthenticationContext";
import OrderFeesReport, { IOrderFeesReport } from "./OrderFeesReport";
import { useHasCustomerExportDataPermission } from "../../hooks/useHasCustomerExportDataPermission";
import { ICustomerData, generateCustomerDataReport } from "./customerDataReport";
interface IReportPageProps {
    venueId: string;
}

export enum ReportType {
    Stock = "reports.Stock",
    Orders = "reports.Orders",
    TotalReport = "reports.TotalReport",
    AreaStock = "reports.AreaStock",
    Pay360TransactionFeeReport = "reports.Pay360TransactionFeeReport",
    VATReport = "reports.VATReport",
    OrderLineItemDetailReport = "reports.OrderLineItemDetailReport",
    OrderFeesReport = "reports.OrderFeesReport",
    CustomerDataExport = "reports.CustomerDataExport"
}

const momentFormat = "YYYY-MM-DDTHH:mm:ss";
const ReportPage: FunctionComponent<IReportPageProps> = ({ venueId }) => {
    const { retrieveAccessToken } = useAuthenticationContext();
    const { hasCustomerExportDataPersmission } = useHasCustomerExportDataPermission();
    const { hasObAccess } = useFlags();
    const classes = useStyles();
    const currentLanguage = useSelector((state: IReduxAppState) => state.language.current);
    const venue = useSelector((state: IReduxAppState) => state.venue[venueId].venueData);
    const hasTransactionFeeReport = (venue?.payment_processor === "PAY360" && venue?.onboarding_source !== OnboardingSource.RMS);
    const { callAPI: getStockReport } = useAPI<IStockReportItem[]>(APIMerchantDashboard.stock, retrieveAccessToken, { preQuery: venueId, onMountDisabled: true, });
    const { callAPI: getOrderReport } = useAPI<IOrderReportItem[]>(APIMerchantDashboard.orderReport, retrieveAccessToken, { preQuery: venueId, onMountDisabled: true, });
    const { callAPI: getTotalReport } = useAPI<any>(APIMerchantDashboard.totalsReport, retrieveAccessToken, { preQuery: venueId, onMountDisabled: true, });
    const { callAPI: getTransactionFeeReport } = useAPI<ITransactionFeeReportItem[]>(APIMerchantDashboard.transactionFeeReport, retrieveAccessToken, { preQuery: venueId, onMountDisabled: true, });
    // TODO add in typings
    const { callAPI: getVatReport } = useAPIV2<IVatReportItem[]>(APIExtensions.vatReport, { onMountDisabled: true, });
    const { callAPI: getDetailedItemReport } = useAPIV2<IOrderLineItemDetailReport[]>(APIExtensions.itemReport, { onMountDisabled: true, yoelloAPIParams:{ venue_id:venueId } });
    const { callAPI: getDFeesReport } = useAPIV2<IOrderFeesReport[]>(APIExtensions.feeReport, { onMountDisabled: true, yoelloAPIParams:{ venue_id:venueId } });
    const { callAPI: getConsumersData } = useAPIV2<ICustomerData[]>(APIExtensions.consumersDataReport, { onMountDisabled: true, yoelloAPIParams:{ venue_id:venueId } });
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const { sendSnackbarMessage } = useSnackbarMessages()!;
    const [reportObject, setReportObject] = useState<IReportObject>({
        start_date_time: moment().subtract(7, "d").minute(0).seconds(0).format(momentFormat),
        end_date_time: moment().minute(0).seconds(0).format(momentFormat),
    });
    const [update, setShouldUpdate] = useState<boolean>(false);
    useEffect(() => {
        //update selected report field if language get changed
        setShouldUpdate(!update)
    // eslint-disable-next-line    
    }, [currentLanguage]);


    const currency = useCurrencyState();

    

    async function onSubmitReport(data: any) {
        setReportObject(data);
        setIsLoading(true);
        const reportObj = data as IReportObject;
        try {
            const startTime = toUTCDate(reportObj.start_date_time);
            const endTime = toUTCDate(reportObj.end_date_time);
            const timezone = moment().tz(moment.tz.guess()).format('z');
            const query: IMessageOptions = { params: { from: startTime, to: endTime, timezone: timezone }, doNotCacheValue: true };
            if (data.report_type === t(ReportType.Stock)) {
                console.log("Stock");
                const stockReportArray = await getStockReport(HTTPMethods.GET, query);
                if (stockReportArray && stockReportArray.length > 0) {
                    await generateStockReportFile(stockReportArray, reportObj, currency,venue?.venue_name?.replace(/\s+/g, '-') as string);
                    sendSnackbarMessage(t('profile.report.snackbardownloading'), "success");
                }
                else {
                    sendSnackbarMessage(t('profile.report.snackbarnoreport'), "warning");
                }
            }
            else if (data.report_type === t(ReportType.VATReport)) {
                // TODO make this more elargent
                const qureyWithVenue = {
                    ...query,
                    params: {
                        ...query.params,
                        venue_id: venueId
                    },
                };
                const vatReportResponse = await getVatReport(HTTPMethods.GET, qureyWithVenue);
                if (vatReportResponse) {
                    const vatReport = vatReportResponse.data as IVatReportItem[];
                    await generateVatReport(vatReport, reportObj,venue?.venue_name?.replace(/\s+/g, '-') as string);
                    sendSnackbarMessage(t('profile.report.snackbardownloading'), "success");
                }
            }
            else if (data.report_type === t(ReportType.TotalReport)) {
                const totalReport = await getTotalReport(HTTPMethods.GET, query);
                if (totalReport) {
                    await generateTotalReport(totalReport, reportObj.start_date_time, reportObj.end_date_time, venue?.payment_processor === "PAY360", venue?.onboarding_source as OnboardingSource ,venue?.venue_name?.replace(/\s+/g, '-') as string , hasObAccess);
                    sendSnackbarMessage(t('profile.report.snackbardownloading'), "success");
                }
                else {
                    sendSnackbarMessage(t('profile.report.snackbarnoreport'), "warning");
                }
            } else if (data.report_type === t(ReportType.Orders)) {
                const orderReportArray = await getOrderReport(HTTPMethods.GET, query) as any;
                if (orderReportArray && orderReportArray.length > 0) {
                    await generateOrderReportFile(orderReportArray, reportObj, currency.iso_code, venue?.onboarding_source === OnboardingSource.RMS, t,venue?.venue_name?.replace(/\s+/g, '-') as string);
                    sendSnackbarMessage(t('profile.report.snackbardownloading'), "success");
                }
                else {
                    sendSnackbarMessage(t('profile.report.snackbarnoreport'), "warning");
                }
            }
            else if (data.report_type === t(ReportType.AreaStock)) {
                const stockArray = await getStockReport(HTTPMethods.GET, { ...query, params: { ...query.params, group_by: "table_area", by_table_area: "true" } });
                console.log({ stockArray });
                if (stockArray && stockArray.length > 0) {
                    await generateStockByAreaReportFile(stockArray as any, reportObj, currency,venue?.venue_name?.replace(/\s+/g, '-') as string);
                    sendSnackbarMessage(t('profile.report.snackbardownloading'), "success");
                }
                else {
                    sendSnackbarMessage(t('profile.report.snackbarnoreport'), "warning");
                }
            } else if (data.report_type === t(ReportType.Pay360TransactionFeeReport)) {
                const transactionFeeArray = await getTransactionFeeReport(HTTPMethods.GET, query);
                if (transactionFeeArray && transactionFeeArray.length > 0) {
                    await generateTransactionFeeReportFile(transactionFeeArray, reportObj,venue?.venue_name?.replace(/\s+/g, '-') as string);
                    sendSnackbarMessage(t('profile.report.snackbardownloading'), "success");
                }
                else {
                    sendSnackbarMessage(t('profile.report.snackbarnoreport'), "warning");
                }
            } else if (data.report_type === t(ReportType.OrderLineItemDetailReport)) {
                const report = await getDetailedItemReport(HTTPMethods.GET, query);
                if (report && report.isSuccessful) {
                    const itemReport = report.data as IOrderLineItemDetailReport[];
                    if(itemReport.length > 0) {
                        await orderLineItemDetailReport(itemReport, reportObj,venue?.venue_name?.replace(/\s+/g, '-') as string, t);
                        sendSnackbarMessage(t('profile.report.snackbardownloading'), "success");
                    }else{
                        sendSnackbarMessage(t('profile.report.snackbarnoreport'), "warning");
                    } 
                }
                else {
                    sendSnackbarMessage(t('profile.report.snackbarnoreport'), "warning");
                }
            } else if (data.report_type === t(ReportType.OrderFeesReport)) {
                const report = await getDFeesReport(HTTPMethods.GET, query);

                if (report && report.isSuccessful) {

                    const feeReport = report.data as IOrderFeesReport[];

                    if(feeReport.length > 0) {
                        await OrderFeesReport(
                            feeReport, 
                            reportObj,
                            venue?.venue_name?.replace(/\s+/g, '-') as string, 
                        );

                        sendSnackbarMessage(t('profile.report.snackbardownloading'), "success");
                    }else{

                        sendSnackbarMessage(t('profile.report.snackbarnoreport'), "warning");
                    } 
                } else {
                    sendSnackbarMessage(t('profile.report.snackbarnoreport'), "warning");
                }
            } else if(data.report_type === t(ReportType.CustomerDataExport)){
                const report = await getConsumersData(HTTPMethods.GET, query);

                if (report && report.isSuccessful) {

                    const consumersReport = report.data as ICustomerData[];
                    if(consumersReport.length > 0) {
                        await generateCustomerDataReport(
                            consumersReport, 
                            startTime,
                            endTime
                        );

                        sendSnackbarMessage(t('profile.report.snackbardownloading'), "success");
                    }else{

                        sendSnackbarMessage(t('profile.report.snackbarnoreport'), "warning");
                    } 
                } else {
                    sendSnackbarMessage(t('profile.report.snackbarnoreport'), "warning");
                }
            }
        } catch (error) {
            console.log({ error });
            sendSnackbarMessage(t('profile.report.snackbarerror'), "error");
        } finally {
            setIsLoading(false);
        }

        // console.log(csvString);
    };
    return <>
        {isLoading ? <CircularProgress /> :
            <Paper className={classes.Container}>
                <DynamicForm 
                    formRows={
                        createReportFormRows(
                            t, 
                            momentFormat, 
                            hasTransactionFeeReport,
                            update, 
                            true,
                            Boolean(hasCustomerExportDataPersmission)
                        )
                    } 
                    formData={reportObject}
                    onHandleSubmit={onSubmitReport} formKeyName="reports"
                    formButtons={{
                        rightButton: {
                            label: t("profile.report.button"), type: "submit", color: EColourPanel.PRIMARY,
                            startIcon: <DownloadSVG />,
                            className: classes.submitButton
                        }
                    }} />
            </Paper>
        }
    </>;
};

export default ReportPage;
